declare global {
  interface HTMLElementTagNameMap {
    "media-banner": MediaBanner;
  }
}

const DEFAULT_BREAKPOINT = 992;

class MediaBanner extends HTMLElement {
  private mediaSlot: HTMLElement | null = null;
  private desktopTemplate: HTMLTemplateElement | null = null;
  private mobileTemplate: HTMLTemplateElement | null = null;
  private mediaQuery: MediaQueryList | null = null;
  private currentVariant: "desktop" | "mobile" | null = null;
  private onBreakpointChange: (event: MediaQueryListEvent) => void;

  constructor() {
    super();
    this.onBreakpointChange = (event) => {
      this.render(event.matches ? "desktop" : "mobile");
    };
  }

  connectedCallback(): void {
    this.mediaSlot = this.querySelector<HTMLElement>(".media-banner__media");
    this.desktopTemplate = this.querySelector<HTMLTemplateElement>(
      'template[data-variant="desktop"]',
    );
    this.mobileTemplate = this.querySelector<HTMLTemplateElement>(
      'template[data-variant="mobile"]',
    );

    if (!this.mediaSlot) return;

    const breakpoint = Number(this.dataset.breakpoint) || DEFAULT_BREAKPOINT;
    this.mediaQuery = window.matchMedia(`(min-width: ${breakpoint}px)`);
    this.mediaQuery.addEventListener("change", this.onBreakpointChange);

    this.render(this.mediaQuery.matches ? "desktop" : "mobile");
  }

  disconnectedCallback(): void {
    if (this.mediaQuery) {
      this.mediaQuery.removeEventListener("change", this.onBreakpointChange);
    }
    this.teardownActiveVideo();
  }

  private render(target: "desktop" | "mobile"): void {
    if (!this.mediaSlot) return;

    // Fall back to the other variant if the requested one has no template
    // (e.g. merchant only configured one device).
    const template = this.pickTemplate(target);
    if (!template) {
      this.teardownActiveVideo();
      this.mediaSlot.replaceChildren();
      this.currentVariant = null;
      return;
    }

    const resolvedVariant: "desktop" | "mobile" =
      template === this.desktopTemplate ? "desktop" : "mobile";
    if (resolvedVariant === this.currentVariant) return;

    this.teardownActiveVideo();
    this.mediaSlot.replaceChildren(template.content.cloneNode(true));
    this.currentVariant = resolvedVariant;

    // Cloning a <video> with autoplay/muted/playsinline triggers playback once
    // it is connected, but Safari can be picky — kick it explicitly.
    const video = this.mediaSlot.querySelector<HTMLVideoElement>("video");
    if (video) {
      video.muted = true;
      video.playsInline = true;
      const playAttempt = video.play();
      if (playAttempt && typeof playAttempt.catch === "function") {
        playAttempt.catch(() => {
          // Autoplay rejected (e.g. low-power mode). Leave the poster visible.
        });
      }
    }
  }

  private pickTemplate(
    target: "desktop" | "mobile",
  ): HTMLTemplateElement | null {
    if (target === "desktop") {
      return this.desktopTemplate ?? this.mobileTemplate;
    }
    return this.mobileTemplate ?? this.desktopTemplate;
  }

  private teardownActiveVideo(): void {
    if (!this.mediaSlot) return;
    const video = this.mediaSlot.querySelector<HTMLVideoElement>("video");
    if (!video) return;
    try {
      video.pause();
      video.removeAttribute("src");
      video.querySelectorAll("source").forEach((source) => source.remove());
      video.load();
    } catch {
      // Some browsers throw on load() after src removal; safe to ignore.
    }
  }
}

if (!customElements.get("media-banner")) {
  customElements.define("media-banner", MediaBanner);
}

export { MediaBanner };
